import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, TENANT_ID } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { traceService } from '../services/traceService';

type DiagnosticResponse = {
  success: boolean;
  traceId: string;
  webhookUrl: string;
  elapsedMs: number;
  ack: { ok: boolean; status: number; body: string; ackMs: number };
  queue: any;
  inbound: any;
  outbound: any;
  timeline: Array<{ step: string; status: 'ok' | 'pending' | 'error'; at: string; details?: any }>;
};

const WebhookFlowDiagnostics: React.FC = () => {
  const { token } = useAuth();
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [tenantId, setTenantId] = useState(TENANT_ID || '');
  const [instanceId, setInstanceId] = useState('');
  const [channelType, setChannelType] = useState<'WEBCHAT' | 'TELEGRAM' | 'WHATSAPP'>('WHATSAPP');
  const [text, setText] = useState('hola, prueba de diagnostico async');
  const [timeoutMs, setTimeoutMs] = useState(12000);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DiagnosticResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await traceService.getTenants();
        if (!cancelled && res.data) {
          setTenants(res.data);
          if (!tenantId && res.data.length > 0) {
            setTenantId(res.data[0]?.id || '');
          }
        }
      } catch {
        // Fallback a entrada manual
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const canRun = useMemo(() => !!tenantId && !!instanceId && !!token, [tenantId, instanceId, token]);

  const runDiagnostic = async () => {
    if (!canRun) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/monitoring/diagnostics/webhook-flow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tenantId,
          instanceId,
          channelType,
          text,
          timeoutMs,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        setError(payload?.error || `Request failed with status ${response.status}`);
        return;
      }

      setResult(payload as DiagnosticResponse);
    } catch (err: any) {
      setError(err?.message || 'Failed to run diagnostic');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overview-page">
      <div className="page-header">
        <h2>Diagnóstico Webhook Async</h2>
      </div>

      <div className="chart-container" style={{ marginBottom: 16 }}>
        <h3>Ejecutar flujo de prueba</h3>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          <label>
            Tenant
            {tenants.length > 0 ? (
              <select
                className="filter-select"
                style={{ width: '100%', marginTop: 4 }}
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
              >
                <option value="">-- Seleccionar Tenant --</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.id.slice(0, 8)}...)
                  </option>
                ))}
              </select>
            ) : (
              <input className="search-input" value={tenantId} onChange={(e) => setTenantId(e.target.value)} />
            )}
          </label>
          <label>
            Instance ID
            <input
              className="search-input"
              placeholder="UUID o slug de instancia de canal"
              value={instanceId}
              onChange={(e) => setInstanceId(e.target.value)}
            />
          </label>
          <label>
            Canal
            <select
              className="filter-select"
              value={channelType}
              onChange={(e) => setChannelType(e.target.value as 'WEBCHAT' | 'TELEGRAM' | 'WHATSAPP')}
            >
              <option value="WHATSAPP">WHATSAPP</option>
              <option value="WEBCHAT">WEBCHAT</option>
              <option value="TELEGRAM">TELEGRAM</option>
            </select>
          </label>
          <label>
            Timeout (ms)
            <input
              className="search-input"
              type="number"
              min={3000}
              max={30000}
              value={timeoutMs}
              onChange={(e) => setTimeoutMs(Number(e.target.value || 12000))}
            />
          </label>
        </div>
        <label style={{ display: 'block', marginTop: 10 }}>
          Texto de prueba
          <input className="search-input" value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        <div style={{ marginTop: 12 }}>
          <button className="refresh-button" disabled={!canRun || loading} onClick={runDiagnostic}>
            {loading ? 'Ejecutando diagnóstico...' : '▶ Ejecutar diagnóstico'}
          </button>
        </div>
      </div>

      {error && (
        <div className="chart-container" style={{ borderColor: '#dc2626', marginBottom: 16 }}>
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      )}

      {result && (
        <>
          <div className="metrics-grid" style={{ marginBottom: 16 }}>
            <div className="metric-card">
              <div className="metric-label">Resultado</div>
              <div className="metric-value">{result.success ? 'OK' : 'FALLA'}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">ACK</div>
              <div className="metric-value">{result.ack.status} ({result.ack.ackMs}ms)</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Elapsed</div>
              <div className="metric-value">{result.elapsedMs}ms</div>
            </div>
          </div>

          {result.traceId && (
            <div style={{ marginBottom: 16 }}>
              <Link
                to={`/admin/debug?traceId=${result.traceId}&tenantId=${tenantId}`}
                className="refresh-button"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                🔍 Inspeccionar Traza en Flow Debugger (#{result.traceId.slice(0, 8)})
              </Link>
            </div>
          )}

          <div className="chart-container" style={{ marginBottom: 16 }}>
            <h3>Trazabilidad</h3>
            <ul className="job-events-list">
              {result.timeline.map((step, idx) => (
                <li key={`${step.step}-${idx}`} className={`job-event ${step.status === 'error' ? 'error' : ''}`}>
                  <span className="job-event-type">{step.step}</span>
                  <span className="job-event-id">{step.status}</span>
                  <span className="job-event-queue">{new Date(step.at).toLocaleTimeString()}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="chart-container">
            <h3>Detalle</h3>
            <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(result, null, 2)}</pre>
          </div>
        </>
      )}
    </div>
  );
};

export default WebhookFlowDiagnostics;
